// The PJ-spec fill-plan table: finished goods as rows, Day 1..5 fill as
// columns, grouped by department, priority order. No customer columns —
// contributing orders live in the drill-down (PJ, 2026-06-26).

import { Fragment, useMemo } from 'react';
import type { BusinessDay, FillPlanSku } from './types';
import { dayTotals } from './fillPlanHelpers';
import styles from './FillPlan.module.css';

const fmt = (n: number) => Math.round(n).toLocaleString('en-US');

function DueCell({ sku, todayIso }: { sku: FillPlanSku; todayIso: string }) {
  const due = sku.earliestRequiredBy;
  if (!due) return <span className={styles.muted}>—</span>;
  if (sku.daysLate > 0) {
    return (
      <span className={styles.duePast}>
        {due} ({sku.daysLate}d late)
      </span>
    );
  }
  const soon =
    Date.parse(due) - Date.parse(todayIso) <= 5 * 86_400_000 ? styles.dueSoon : '';
  return <span className={soon}>{due}</span>;
}

function FlagsCell({ sku }: { sku: FillPlanSku }) {
  return (
    <span className={styles.flags}>
      {sku.criticalPo && (
        <span
          className={`${styles.badge} ${styles.badgeCrit}`}
          title={sku.poLandingDate ? `PO lands ${sku.poLandingDate}` : 'critical PO'}
        >
          critical PO
        </span>
      )}
      {sku.mtoStatus?.startsWith('MTO') && (
        <span className={`${styles.badge} ${styles.badgeMto}`}>{sku.mtoStatus}</span>
      )}
      {sku.fillPeriod !== null && (
        <span className={styles.seq} title="day-1 run order">
          {sku.fillPeriod}
        </span>
      )}
    </span>
  );
}

export function FillPlanTable({
  skus,
  days,
  todayIso,
  selectedSku,
  onSelectSku,
}: {
  skus: readonly FillPlanSku[];
  days: readonly BusinessDay[];
  todayIso: string;
  selectedSku: string | null;
  onSelectSku: (sku: string) => void;
}) {
  // Preserve the incoming (already sorted) order inside each department group.
  const groups = useMemo(() => {
    const byDept = new Map<string, FillPlanSku[]>();
    for (const s of skus) {
      const g = byDept.get(s.department);
      if (g) g.push(s);
      else byDept.set(s.department, [s]);
    }
    return [...byDept.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [skus]);

  if (!skus.length) {
    return <div className={styles.empty}>No fill-plan lines match.</div>;
  }

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>Finished good</th>
          {days.map((d) => (
            <th key={d.n} className={styles.num}>
              Day {d.n} fill
              <div className={styles.thDate}>{d.label}</div>
            </th>
          ))}
          <th className={styles.num}>5-day total</th>
          <th className={styles.num}>On hand</th>
          <th>Needed by</th>
          <th>Flags</th>
        </tr>
      </thead>
      <tbody>
        {groups.map(([dept, group]) => {
          const totals = dayTotals(group);
          return (
            <Fragment key={dept}>
              <tr className={styles.groupRow}>
                <td colSpan={10}>
                  {dept}{' '}
                  <span className={styles.groupCount}>
                    — {group.length} finished good{group.length === 1 ? '' : 's'},
                    priority order
                  </span>
                </td>
              </tr>
              {group.map((s) => (
                <tr
                  key={s.sku}
                  className={s.sku === selectedSku ? styles.rowSelected : ''}
                  onClick={() => onSelectSku(s.sku)}
                >
                  <td>
                    <div className={styles.prod}>{s.name}</div>
                    <div className={styles.code}>
                      {s.code} · {s.orders.length} order line
                      {s.orders.length === 1 ? '' : 's'}
                    </div>
                  </td>
                  {days.map((d) => (
                    <td key={d.n} className={styles.num}>
                      {s.dayUnits[d.n] > 0 ? (
                        <span className={styles.qty}>{fmt(s.dayUnits[d.n])}</span>
                      ) : (
                        <span className={styles.zero}>—</span>
                      )}
                    </td>
                  ))}
                  <td className={styles.num}>
                    <b>{fmt(s.totalUnits)}</b>
                  </td>
                  <td className={styles.num}>
                    {fmt(s.fgInv)}
                    {s.fgInv >= s.totalUnits && s.totalUnits > 0 && (
                      <div className={styles.coverOk}>≥ plan</div>
                    )}
                  </td>
                  <td>
                    <DueCell sku={s} todayIso={todayIso} />
                  </td>
                  <td>
                    <FlagsCell sku={s} />
                  </td>
                </tr>
              ))}
              <tr className={styles.totalRow}>
                <td>{dept} total</td>
                {days.map((d) => (
                  <td key={d.n} className={styles.num}>
                    {totals[d.n] > 0 ? fmt(totals[d.n]) : '—'}
                  </td>
                ))}
                <td className={styles.num}>
                  <b>{fmt(totals.slice(1).reduce((a, x) => a + x, 0))}</b>
                </td>
                <td colSpan={3} />
              </tr>
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}
